<template>
    <v-dialog
        v-model="dialogModel"
        width="768"
        max-width="95%"
        @click:outside.prevent="closeDialog"
    >
        <v-card>
            <v-card-title class="text-h6 text-md-h5 grey lighten-2">
                <v-row>
                    <v-col cols="10">
                        New Employee Form
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="py-5" style="max-height: 75vh; overflow-y: auto;">
                <v-form ref="newEmployeeForm" lazy-validation>
                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Business Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.businessName"
                                placeholder="Business Name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <div class="mb-4" style="border-bottom:1px solid #e0e0e0">
                        <h2 class="mb-2">Personal Details</h2>
                    </div>

                    <v-row class="mb-4">
                        <v-col cols="12" sm="6">
                            <h3 class="mb-4">Title <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.name.title"
                                placeholder="Title"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Title"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <h3 class="mb-4">First Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.name.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                :rules="firstNameRules"
                                hint="First Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <h3 class="mb-4">Middle Name(s)</h3>
                            <v-text-field
                                v-model="form.name.middleNames"
                                placeholder="Middle Name(s)"
                                outlined
                                dense
                                hint="Middle Name(s)"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <h3 class="mb-4">Surname <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.name.surname"
                                placeholder="Surname"
                                outlined
                                dense
                                :rules="lastNameRules"
                                hint="Surname"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row dense class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Date of Birth <span class="red--text">*</span></h3>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-menu
                                v-model="dobMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.dateOfBirthFormatted"
                                        placeholder="DD/MM/YYYY"
                                        readonly
                                        outlined
                                        dense
                                        prepend-icon="mdi-calendar"
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRules]"
                                        hint="Date"
                                        :persistent-hint="true"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="form.dateOfBirth"
                                    :max="today"
                                    @input="dobMenu = false"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Age <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.age"
                                placeholder="Example: 25"
                                outlined
                                dense
                                type="number"
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Gender <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.gender" row :rules="[requiredRules]">
                                <v-radio label="Male" value="male" />
                                <v-radio label="Female" value="female" />
                            </v-radio-group>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4 align-end">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Address<span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.address.line1"
                                placeholder="Street Address"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Street Address"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="form.address.line2"
                                placeholder="Street Address Line 2"
                                outlined
                                dense
                                hint="Street Address Line 2"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.address.city"
                                placeholder="City"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="City"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.address.state"
                                placeholder="State / Province"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="State / Province"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.address.postalCode"
                                placeholder="Postal / Zip Code"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Postal / Zip Code"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Email <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.email"
                                placeholder="example@example.com"
                                outlined
                                dense
                                :rules="emailRules"
                                hint="example@example.com"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-2">
                        <v-col cols="12">
                            <h3 class="mb-2">Days Worked <span class="red--text">*</span></h3>
                            <CheckboxGroupField
                                v-model="form.daysWorked"
                                :items="daysOfWeek"
                                :md-cols="3"
                                :rules="daysWorkedRules"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Hours Per Week <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.hoursPerWeek"
                                placeholder="Hours Per Week"
                                outlined
                                dense
                                type="number"
                                :rules="[requiredRules]"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Days Worked Per Week <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.daysWorkedPerWeek"
                                placeholder="Days Worked Per Week"
                                outlined
                                dense
                                type="number"
                                :rules="daysWorkedPerWeekRules"
                            />
                        </v-col>
                    </v-row>

                    <div class="mb-4 pt-5" style="border-bottom:1px solid #e0e0e0">
                        <h2 class="mb-2">Starter / Leaver</h2>
                    </div>

                    <v-row dense class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Start Date <span class="red--text">*</span></h3>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-menu
                                v-model="startDateMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.startDateFormatted"
                                        placeholder="DD/MM/YYYY"
                                        readonly
                                        outlined
                                        dense
                                        prepend-icon="mdi-calendar"
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRules]"
                                        hint="Date"
                                        :persistent-hint="true"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="form.startDate"
                                    @input="startDateMenu = false"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Starter Declaration <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.starterDeclaration" :rules="[requiredRules]">
                                <v-radio
                                    label="This is my first job since 6th April this year"
                                    value="first-job-since-6-april"
                                />
                                <v-radio
                                    label="This is my only job now, but I've had another job since 6th April"
                                    value="only-job-now-had-another-since-6-april"
                                />
                                <v-radio
                                    label="I have another job"
                                    value="another-job"
                                />
                            </v-radio-group>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Gross Pay</h3>
                            <v-text-field
                                v-model="form.grossPay"
                                placeholder="Gross Pay"
                                outlined
                                dense
                                type="number"
                                :rules="moneyFieldRules"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Gross Tax</h3>
                            <v-text-field
                                v-model="form.grossTax"
                                placeholder="Gross Tax"
                                outlined
                                dense
                                type="number"
                                :rules="moneyFieldRules"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Pay Frequency <span class="red--text">*</span></h3>
                            <v-select
                                v-model="form.payFrequency"
                                :items="payFrequencyItems"
                                outlined
                                dense
                                attach
                                placeholder="Please Select"
                                :rules="[requiredRules]"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Method of Pay <span class="red--text">*</span></h3>
                            <v-select
                                v-model="form.methodOfPay"
                                :items="methodOfPayItems"
                                outlined
                                dense
                                attach
                                placeholder="Please Select"
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Hourly Rate <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.hourlyRate"
                                placeholder="Hourly Rate"
                                outlined
                                dense
                                type="number"
                                :rules="hourlyRateRules"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">National Insurance Number <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nationalInsuranceNumber"
                                placeholder="Insurance Number"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Do You Have a Student Loan? <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.hasStudentLoan" row :rules="[requiredRules]">
                                <v-radio label="Yes" value="yes" />
                                <v-radio label="No" value="no" />
                            </v-radio-group>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">If Yes, Select Plan <span class="red--text">*</span></h3>
                            <v-select
                                v-model="form.studentLoanPlan"
                                :items="studentLoanPlanItems"
                                outlined
                                dense
                                placeholder="Please Select"
                                :rules="[value => form.hasStudentLoan !== 'yes' || !!value || 'Required']"
                                attach
                            />
                        </v-col>
                    </v-row>

                    <v-row dense class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Date <span class="red--text">*</span></h3>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-menu
                                v-model="dateMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.dateFormatted"
                                        placeholder="Date"
                                        readonly
                                        outlined
                                        dense
                                        prepend-icon="mdi-calendar"
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRules]"
                                        hint="Date"
                                        :persistent-hint="true"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="form.date"
                                    @input="dateMenu = false"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>

                    <div class="mt-5 pt-5" style="border-top:1px solid #e0e0e0">
                        <div class="d-flex justify-end">
                            <v-btn color="primary" :loading="submitLoading" @click="onSubmit">
                                Submit
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import { mapActions } from 'vuex'
    import validationRules from '@/common/validationRules'
    import CheckboxGroupField from '@/components/common/CheckboxGroupField.vue'

    const daysOfWeek = [
        { label: 'Mon', value: 'mon' },
        { label: 'Tue', value: 'tue' },
        { label: 'Wed', value: 'wed' },
        { label: 'Thu', value: 'thu' },
        { label: 'Fri', value: 'fri' },
        { label: 'Sat', value: 'sat' },
        { label: 'Sun', value: 'sun' }
    ]

    const emptyForm = () => ({
        businessName: '',
        name: {
            title: '',
            firstName: '',
            middleNames: '',
            surname: ''
        },
        dateOfBirth: null,
        dateOfBirthFormatted: '',
        age: '',
        gender: '',
        address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postalCode: ''
        },
        email: '',
        daysWorked: [],
        hoursPerWeek: '',
        daysWorkedPerWeek: '',
        startDate: null,
        startDateFormatted: '',
        starterDeclaration: '',
        grossPay: '',
        grossTax: '',
        payFrequency: '',
        methodOfPay: '',
        hourlyRate: '',
        nationalInsuranceNumber: '',
        hasStudentLoan: '',
        studentLoanPlan: '',
        date: null,
        dateFormatted: ''
    })

    export default {
        name: 'NewEmployeeForm',
        props: ['isOpen', 'programme'],
        components: {
            CheckboxGroupField
        },
        data: () => ({
            form: emptyForm(),
            submitLoading: false,
            dateMenu: false,
            dobMenu: false,
            startDateMenu: false,
            firstNameRules: validationRules.firstName,
            lastNameRules: validationRules.lastName,
            emailRules: validationRules.email,
            requiredRules: validationRules.required,
            daysOfWeek,
            payFrequencyItems: ['Monthly'],
            methodOfPayItems: ['Bank Transfer'],
            studentLoanPlanItems: ['1', '2'],
            daysWorkedPerWeekRules: [
                validationRules.required,
                value => Number(value) > 0 || 'Days worked per week must be greater than 0',
                value => Number(value) <= 7 || 'Days worked per week must be 7 or less'
            ],
            daysWorkedRules: [
                value => (Array.isArray(value) && value.length > 0) || 'Required'
            ],
            hourlyRateRules: [
                validationRules.required,
                value => Number(value) > 0 || 'Hourly rate must be greater than 0'
            ],
            moneyFieldRules: [
                value => !String(value || '').trim() || Number(value) >= 0 || 'Value must be 0 or greater'
            ]
        }),
        computed: {
            dialogModel: {
                get () {
                    return this.isOpen
                },
                set (value) {
                    this.$emit('update:isOpen', value)
                }
            },
            today () {
                return new Date().toISOString().slice(0, 10)
            }
        },
        watch: {
            'form.date' (val) {
                this.form.dateFormatted = val ? Date.formatDate([val]).trim() : ''
            },
            'form.dateOfBirth' (val) {
                this.form.dateOfBirthFormatted = val ? Date.formatDate([val]).trim() : ''
            },
            'form.startDate' (val) {
                this.form.startDateFormatted = val ? Date.formatDate([val]).trim() : ''
            }
        },
        methods: {
            ...mapActions([
                'uploadNewEmployeeForm', 'showAlert'
            ]),
            getStarterDeclarationOption (value) {
                const optionsMap = {
                    'first-job-since-6-april': 1,
                    'only-job-now-had-another-since-6-april': 2,
                    'another-job': 3
                }

                return optionsMap[value] || 0
            },
            buildPayload () {
                const hasStudentLoan = this.form.hasStudentLoan === 'yes'
                return {
                    transitionProgrammeCourseId: this.programme && this.programme.id ? this.programme.id : 0,
                    businessName: this.form.businessName,
                    titleFirstName: `${this.form.name.title} ${this.form.name.firstName}`.trim(),
                    middleNames: this.form.name.middleNames,
                    surname: this.form.name.surname,
                    dateOfBirth: this.form.dateOfBirth,
                    age: Number(this.form.age),
                    gender: this.form.gender,
                    streetAddress: this.form.address.line1,
                    streetAddressLine2: this.form.address.line2,
                    city: this.form.address.city,
                    stateProvince: this.form.address.state,
                    postalZipCode: this.form.address.postalCode,
                    email: this.form.email,
                    daysWorked: this.form.daysWorked.join(','),
                    hoursPerWeek: String(this.form.hoursPerWeek || ''),
                    daysWorkedPerWeek: String(this.form.daysWorkedPerWeek || ''),
                    starterLeaver: 'Starter',
                    startDate: this.form.startDate,
                    starterDeclaration: this.getStarterDeclarationOption(this.form.starterDeclaration),
                    grossPay: String(this.form.grossPay || ''),
                    grossTax: String(this.form.grossTax || ''),
                    payFrequency: this.form.payFrequency,
                    methodOfPay: this.form.methodOfPay,
                    hourlyRate: String(this.form.hourlyRate || ''),
                    nationalInsuranceNumber: this.form.nationalInsuranceNumber,
                    doYouHaveStudentLoan: hasStudentLoan,
                    studentLoanPlan1: hasStudentLoan && this.form.studentLoanPlan === '1',
                    studentLoanPlan2: hasStudentLoan && this.form.studentLoanPlan === '2',
                    date: this.form.date,
                    formType: 'NewEmployeeForm'
                }
            },
            resetForm () {
                this.form = emptyForm()
            },
            closeDialog () {
                this.resetForm()
                this.$emit('update:isOpen', false)
            },
            async onSubmit () {
                const formRef = this.$refs.newEmployeeForm
                if (!formRef.validate()) {
                    this.showAlert({
                        text: 'Fill all the required fields please',
                        type: 'error'
                    })
                    return
                }

                this.submitLoading = true
                try {
                    const payload = this.buildPayload()
                    await this.uploadNewEmployeeForm(payload)
                    this.$emit('saved')
                    this.closeDialog()
                    if (formRef && formRef.resetValidation) formRef.resetValidation()
                } catch (e) {
                    this.showAlert({
                        text: e.message || 'Error saving form',
                        type: 'error'
                    })
                } finally {
                    this.submitLoading = false
                }
            }
        }
    }
</script>
